'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';
import { Clipboard, Check } from 'lucide-react';

import useBoop from '@/hooks/use-boop';
import useHasHydrated from '@/hooks/use-has-hydrated';

import {
  InfoAside,
  WarningAside,
  SuccessAside,
} from '@/components/Aside';
import VisuallyHidden from '@/components/VisuallyHidden';
import Spinner from '@/components/Spinner';

import AnnotatedContainer from './AnnotatedContainer';

interface Props extends React.HTMLAttributes<HTMLButtonElement> {
  handleCopy: (copyId: number) => void;
}

function CopyButton({ handleCopy, ...delegated }: Props) {
  const [copyId, setCopyId] = React.useState<number | null>(null);
  const [rotation, setRotation] = React.useState(0);
  const [wrapperStyle, wrapperTrigger] = useBoop({ scale: 1.05 });

  const hasHydrated = useHasHydrated();

  const style = useSpring({
    transform: `rotate(${rotation}deg)`,
    config: {
      tension: 300,
      friction: 10,
    },
  });

  const timeoutId = React.useRef<number>();

  React.useEffect(() => {
    if (!copyId) {
      return;
    }

    // let timeoutId: number;
    timeoutId.current = window.setTimeout(() => {
      setCopyId(null);
    }, 3000);

    return () => {
      if (timeoutId.current) {
        window.clearTimeout(timeoutId.current);
      }
    };
  }, [copyId]);

  return (
    <Wrapper
      {...delegated}
      onMouseDown={() => {
        setRotation(10);
      }}
      onMouseEnter={wrapperTrigger}
      onClick={() => {
        const newCopyId = Date.now();
        handleCopy(newCopyId);
        setRotation(-10);

        window.setTimeout(() => {
          setRotation(0);
        }, 150);

        window.setTimeout(() => {
          setCopyId(newCopyId);
        }, 0);
      }}
    >
      <IconOuterWrapper style={wrapperStyle}>
        <IconWrapper style={style}>
          {/* We never want there to be confusion around whether the app is interactive or not. Render a spinner which will be replaced after hydration */}
          {hasHydrated ? (
            <Clipboard size={24} />
          ) : (
            <Spinner size={24} />
          )}
          <CheckWrapper
            style={{
              opacity: copyId ? 1 : 0,
              transform: copyId ? 'scale(1)' : 'scale(0)',
            }}
          >
            <Check size={12} strokeWidth={4} />
          </CheckWrapper>
          <HiddenText>Copy to clipboard</HiddenText>
        </IconWrapper>
      </IconOuterWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.button`
  position: sticky;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  width: var(--copy-button-size);
  height: var(--copy-button-size);
  /*
    --copy-button-sticky-top is used when this code snippet is rendered on a page with a sticky header (eg. within blog posts). It ensures the button appears to stack below the header. It's set in CodeWrapper.
    --copy-button-margin is the buffer between the copy button and the edge of the snippet. It's defined in CodeWrapper, since it's also used there for some calculations.
  */
  top: calc(
    var(--copy-button-sticky-top, 0px) + var(--copy-button-margin)
  );
  transform: translateX(calc(var(--copy-button-margin) * -1));
  margin-block: var(--copy-button-margin);
  margin-left: auto;
  margin-right: 0;
  border: 1px solid;
  color: var(--color-gray-800);
  border-radius: 5px;
  opacity: 0;
  transition: opacity 250ms;
  transition-delay: 1000ms;

  ${AnnotatedContainer}:hover &,
  &:focus-visible {
    opacity: 1;
    transition-delay: 0ms;
  }

  /*
    The color mode can be set in two ways:
    • The global page-level value, on the <html> tag
    • A local value, on this element via data-locked-to-color-mode

    This complicated selector ensures that we prioritize the local value, while still following the global value otherwise
  */
  html[data-color-mode='light']
    &:not([data-locked-to-color-mode='dark']),
  &[data-locked-to-color-mode='light'] {
    background: var(--color-info-300);
    border-color: var(--color-info-100);
  }
  html[data-color-mode='dark']
    &:not([data-locked-to-color-mode='light']),
  &[data-locked-to-color-mode='dark'] {
    background: var(--color-background);
    border-color: var(--color-gray-200);
  }

  html[data-color-mode='dark'] ${InfoAside} & {
    border-color: var(--color-info-300);
    background: var(--color-info-200);
  }
  html[data-color-mode='dark'] ${WarningAside} & {
    border-color: var(--color-warning-300);
    background: var(--color-warning-200);
  }
  html[data-color-mode='dark'] ${SuccessAside} & {
    border-color: var(--color-success-300);
    background: var(--color-success-200);
  }

  html[data-color-mode='light'] ${InfoAside} & {
    border-color: var(--color-info-200);
    background: var(--color-info-300);
  }
  html[data-color-mode='light'] ${WarningAside} & {
    border-color: var(--color-warning-200);
    background: var(--color-warning-300);
  }
  html[data-color-mode='light'] ${SuccessAside} & {
    border-color: var(--color-success-200);
    background: var(--color-success-300);
  }
`;

const IconOuterWrapper = styled(animated.span)`
  display: block;
  will-change: transform;
`;

const IconWrapper = styled(animated.span)`
  display: block;
  will-change: transform;
  transform-origin: 50% 20%;

  svg {
    display: block;
  }
`;

const CheckWrapper = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  top: 2px;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: hsl(180deg 100% 30%);
  color: oklch(0.59 0.14 194.88);
  transition:
    opacity 500ms,
    transform 650ms;
  transition-delay: 200ms;
`;

const HiddenText = styled(VisuallyHidden)`
  /* If the user wants to copy the code by selecting all of the stuff inside, we don't want them to also copy the “Copy to clipboard” hidden text: */
  user-select: none;
`;

export default CopyButton;
